import React from 'react';
import { pxToRem } from '@withjoy/joykit/theme';
import { ChevronDown } from '@withjoy/joykit/icons';
import { Box, Flex, PopoverV2, TextV2, useDisclosure } from '@withjoy/joykit';

import { useTranslation } from '@shared/core';
import { CarListingEntry } from './CarListingEntry';
import { useCarSortOptions } from '../../hooks/useCarSortOptions';
import { DropdownButton } from '@apps/stays/components/DropdownButton';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import type { CarRatesSortDto, CarSearchLocation, CarVehicle } from '../../CarRentalConsole.types';

type CarListingsPanelProps = Readonly<{
  navigatorLanguage: string;
  sortOption: CarRatesSortDto;
  reservingOfferToken: string | null;
  vehicles: ReadonlyArray<CarVehicle>;
  onReserve: (vehicle: CarVehicle) => void;
  onViewDetails: (vehicle: CarVehicle) => void;
  onSortChange: (sort: CarRatesSortDto) => void;
  searchedPickUpLocation: CarSearchLocation | null;
  searchedDropOffLocation: CarSearchLocation | null;
}>;

const Container: React.FC<{}> = ({ children }) => (
  <Flex
    flex={1}
    rowGap={6}
    width="full"
    height="fit-content"
    flexDirection="column"
    paddingX={{ _: 4, sm2: 8 }}
    paddingY={{ _: 6, sm2: 8 }}
  >
    {children}
  </Flex>
);

const SortControl = ({ sortOption, onSortChange }: Pick<CarListingsPanelProps, 'sortOption' | 'onSortChange'>) => {
  const { tq } = useTranslation('carRentalConsole');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const options = useCarSortOptions();

  const selectedLabel = options.find(option => option.value === sortOption)?.label ?? options[0].label;

  return (
    <Flex position="relative">
      <Box onClick={onOpen}>
        <DropdownButton
          endIcon={<ChevronDown size={16} />}
          text={`${tq`Sort:`} ${selectedLabel}`}
        />
      </Box>
      <PopoverV2
        isOpen={isOpen}
        onOpen={onOpen}
        onClose={onClose}
        placement="bottom"
        overrides={{
          Root: { props: { minWidth: pxToRem(220), right: 0 } },
          Content: { props: { borderRadius: pxToRem(16), style: { userSelect: 'none' } } },
        }}
      >
        <PopoverV2.Content>
          <Box padding={5}>
            <Flex
              gap={3}
              flexDirection="column"
            >
              {options.map(option => (
                <Box
                  paddingY={2}
                  cursor="pointer"
                  key={option.value}
                  color={option.value === sortOption ? 'newUITest1' : 'mono14'}
                  typographyVariant={option.value === sortOption ? 'button2' : 'body1'}
                  onClick={() => {
                    onSortChange(option.value);
                    onClose();
                  }}
                >
                  {option.label}
                </Box>
              ))}
            </Flex>
          </Box>
        </PopoverV2.Content>
      </PopoverV2>
    </Flex>
  );
};

const CarListingsPanelInner = ({
  vehicles,
  onReserve,
  onViewDetails,
  sortOption,
  onSortChange,
  navigatorLanguage,
  reservingOfferToken,
  searchedPickUpLocation,
  searchedDropOffLocation,
}: CarListingsPanelProps) => {
  const { tq } = useTranslation('carRentalConsole');
  const isMobileOrTablet = useIsMobileOrTablet();

  return (
    <Container>
      <Flex
        alignItems="center"
        justifyContent="space-between"
      >
        <Flex
          alignItems="center"
          columnGap={isMobileOrTablet ? 6 : 4}
        >
          <TextV2 typographyVariant="hed4">{tq`Browse Car Rentals`}</TextV2>
          <TextV2 typographyVariant="body1">{tq.plural(vehicles.length, { one: tq`${vehicles.length} result`, other: tq`${vehicles.length} results` })}</TextV2>
        </Flex>
        {!isMobileOrTablet && (
          <SortControl
            sortOption={sortOption}
            onSortChange={onSortChange}
          />
        )}
      </Flex>
      {vehicles.map(vehicle => (
        <CarListingEntry
          vehicle={vehicle}
          onReserve={onReserve}
          key={vehicle.offerToken}
          onViewDetails={onViewDetails}
          navigatorLanguage={navigatorLanguage}
          searchedPickUpLocation={searchedPickUpLocation}
          searchedDropOffLocation={searchedDropOffLocation}
          isReserving={reservingOfferToken === vehicle.offerToken}
          isReserveDisabled={reservingOfferToken !== null && reservingOfferToken !== vehicle.offerToken}
        />
      ))}
    </Container>
  );
};

export const CarListingsPanel = React.memo(CarListingsPanelInner);
